import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureResultPanel as captureResultPanelCanvas,
  createResultFiles,
  downloadResultFile,
  type ExportFormat
} from "../utils/resultExport";
import type { UseResultExportOptions } from "../type/result";

export function useResultExport({
  panelSelector,
  actionsClassName,
  extraFooterText,
  getFileBaseName,
  onExported
}: UseResultExportOptions) {
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState<ExportFormat | null>(null);
  const exportMenuRef = useRef<HTMLDivElement | null>(null);
  const resultPanelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isExportMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (exportMenuRef.current?.contains(event.target as Node)) {
        return;
      }
      setIsExportMenuOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsExportMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isExportMenuOpen]);

  // Zwraca canvas już ze stopką — ten sam dla pobierania i dla wysyłki e-mail.
  const captureResultPanel = useCallback(
    () =>
      captureResultPanelCanvas({
        container: resultPanelRef.current,
        panelSelector,
        actionsClassName,
        extraFooterText
      }),
    [panelSelector, actionsClassName, extraFooterText]
  );

  const handleExport = async (format: ExportFormat) => {
    setIsExportMenuOpen(false);
    setIsExporting(true);
    setExportFormat(format);

    try {
      const canvas = await captureResultPanel();
      downloadResultFile(createResultFiles(canvas), format, getFileBaseName());
      onExported?.(format);
    } catch (error) {
      console.error("Failed to export result:", error);
    } finally {
      setExportFormat(null);
      setIsExporting(false);
    }
  };

  return {
    isExportMenuOpen,
    setIsExportMenuOpen,
    isExporting,
    exportFormat,
    exportMenuRef,
    resultPanelRef,
    handleExport,
    captureResultPanel
  };
}